import SessionElapsedTime from '@github-ui/agent-sessions/components/SessionElapsedTime'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {testIdProps} from '@github-ui/test-id-props'
import {AnchoredOverlay} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useRef, useState} from 'react'

import styles from './agent-task-popover.module.css'

// Delay before showing the popover to prevent rapid tooltip flashing when hovering across rows
const POPOVER_SHOW_DELAY_MS = 50
// Delay before hiding the popover to allow mouse movement from anchor to overlay content
const POPOVER_HIDE_DELAY_MS = 200

export interface AgentTaskPopoverProps {
  title?: string
  nwo?: string
  state: SessionState
  createdAt?: string
  completedAt?: string
  children: React.ReactNode
  /** Optional className for the popover anchor wrapper div */
  className?: string
  /** Optional URL to navigate to when the popover content is clicked */
  href?: string
  /** Optional target attribute for the popover link */
  target?: string
  /** Optional rel attribute for the popover link */
  rel?: string
}

function PopoverContent({
  href,
  target,
  rel,
  title,
  nwo,
  state,
  createdAt,
  completedAt,
  showPopover,
  hidePopover,
}: {
  href?: string
  target?: string
  rel?: string
  title?: string
  nwo?: string
  state: SessionState
  createdAt?: string
  completedAt?: string
  showPopover: () => void
  hidePopover: () => void
}) {
  const content = (
    <>
      <div className={styles.header}>
        {title && <span className={styles.title}>{title}</span>}
        {createdAt && (
          <span className={styles.elapsedTime}>
            <SessionElapsedTime createdAt={createdAt} completedAt={completedAt} state={state} />
          </span>
        )}
      </div>
      {nwo && <div className={styles.subtitle}>{nwo}</div>}
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={clsx(styles.popoverContent, styles.popoverLink)}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocus={showPopover}
        onBlur={hidePopover}
        {...testIdProps('agent-task-popover')}
      >
        {content}
      </a>
    )
  }

  return (
    <div
      className={styles.popoverContent}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onFocus={showPopover}
      onBlur={hidePopover}
      {...testIdProps('agent-task-popover')}
    >
      {content}
    </div>
  )
}

export const AgentTaskPopover = memo(function AgentTaskPopover({
  title,
  nwo,
  state,
  createdAt,
  completedAt,
  children,
  className,
  href,
  target,
  rel,
}: AgentTaskPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showPopover = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) return
    showTimeoutRef.current = setTimeout(() => {
      showTimeoutRef.current = null
      setIsOpen(true)
    }, POPOVER_SHOW_DELAY_MS)
  }, [])

  const showPopoverImmediate = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    setIsOpen(true)
  }, [])

  const hidePopover = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, POPOVER_HIDE_DELAY_MS)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  if (!title && !nwo) {
    return <>{children}</>
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={clsx(styles.popoverWrapper, className)}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocusCapture={showPopoverImmediate}
        onBlurCapture={hidePopover}
        {...testIdProps('agent-task-popover-wrapper')}
      >
        {children}
      </div>
      <AnchoredOverlay
        open={isOpen}
        renderAnchor={null}
        anchorRef={anchorRef}
        onClose={handleClose}
        focusTrapSettings={{disabled: true}}
        side="outside-top"
        align="center"
        overlayProps={{
          preventFocusOnOpen: true,
          role: 'tooltip',
        }}
      >
        <PopoverContent
          href={href}
          target={target}
          rel={rel}
          title={title}
          nwo={nwo}
          state={state}
          createdAt={createdAt}
          completedAt={completedAt}
          showPopover={showPopover}
          hidePopover={hidePopover}
        />
      </AnchoredOverlay>
    </>
  )
})
